import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { BaseService } from './base.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from './settings.service';
import { take } from 'rxjs/operators';
import { ErpWarehouseService } from './erp/erp-warehouse.service';
import { ERPWarehouse, ERPWhStocks } from '../models/erpwarehouse';
import { EntityService } from './entity.service';
import { ConfQuote, ConfQuoteLine, Field } from '../model';
import { FileUploadService } from './file-upload.service';
import { QuoteService } from './quote.service';
import { NotificationService } from './notification.service';
import { EntityItemFilter } from '../common/filters';
import { firstValueFrom } from 'rxjs';
import { ProductListHelper } from '../common/helper';
import { MeasurementUnit } from '../common/enums';
import { ProductListLineTotalPricePipe } from '../pipes/product-list-line-total-price.pipe';
import { MatDialog } from '@angular/material/dialog';
import { ProductWarehouseStockInfoComponent } from '../common/entity/data-item/fields/productlist-field/product-warehouse-stock-info/product-warehouse-stock-info.component';
import { ErpWarehouseProductStock } from '../models/erp-warehouse-product-stock';

@Injectable({
  providedIn: 'root',
})
export class ProductListService extends BaseService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly translateService: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly erpWarehouseService: ErpWarehouseService,
    private readonly entityService: EntityService,
    private readonly fileUploadService: FileUploadService,
    private readonly quoteService: QuoteService,
    private readonly notificationService: NotificationService,
    public readonly dialog: MatDialog,
    private readonly warehouseService: ErpWarehouseService
  ) {
    super(httpClient);
  }

  private readonly productListLineTotalPricePipe =
    new ProductListLineTotalPricePipe();
  addressItem: any;
  addressId: string | null = null;
  productListWarehouses = signal<ERPWarehouse[]>([]);
  productFields = signal<Field[]>([]);
  productStocksPerWarehouse = signal<ERPWhStocks[]>([]);
  warehouseProductStocks = signal<ErpWarehouseProductStock[]>([]);

  public makeGridColumns() {
    return [
      {
        key: 'id',
        desc: '#',
      },
      {
        key: 'product_name',
        desc: this.translateService.instant('QuoteConfigurator.prodName'),
      },
      {
        key: 'qty',
        desc: this.translateService.instant('QuoteConfigurator.qty'),
      },
      {
        key: 'unit',
        desc: this.translateService.instant('QuoteConfigurator.unit'),
      },
      {
        key: 'discount',
        desc: this.translateService.instant('QuoteConfigurator.discount'),
      },
      {
        key: 'discount2',
        desc:
          this.translateService.instant('QuoteConfigurator.discount') + ' ' + 2,
      },
      {
        key: 'customPrice',
        desc: this.translateService.instant('ProdConfigurator.price'),
      },
      {
        key: 'vat',
        desc: this.translateService.instant('QuoteConfigurator.vat'),
      },
      {
        key: 'backorder',
        desc: this.translateService.instant('ProdConfigurator.backorder'),
      },
      {
        key: 'piecePrice',
        desc: this.translateService.instant('QuoteConfigurator.piecePrice'),
      },
      {
        key: 'smallBoxPrice',
        desc: this.translateService.instant('QuoteConfigurator.smallBoxPrice'),
      },
      {
        key: 'boxPrice',
        desc: this.translateService.instant('QuoteConfigurator.boxPrice'),
      },
      {
        key: 'paletPrice',
        desc: this.translateService.instant('QuoteConfigurator.paletPrice'),
      },
      {
        key: 'nettoPrice',
        desc: this.translateService.instant('ProdConfigurator.NettoPrice'),
      },
      {
        key: 'stock',
        desc: this.translateService.instant('QuoteConfigurator.stock'),
      },
      {
        key: 'availability',
        desc: this.translateService.instant('QuoteConfigurator.availability'),
      },
      {
        key: 'totalPieces',
        desc: this.translateService.instant('QuoteConfigurator.totalPieces'),
      },
      {
        key: 'totalPrice',
        desc: this.translateService.instant('QuoteConfigurator.totalPrice'),
      },
      {
        key: 'warehouse',
        desc: this.translateService.instant('ERPWarehouse.warehouse'),
      },
      {
        key: 'containers',
        desc: this.translateService.instant('QuoteConfigurator.containers'),
      },
      {
        key: 'extranumeric1',
        desc: this.settingsService.getSavedSettings('quoteConfNumFld1')[0]
          ? this.settingsService.getSavedSettings('quoteConfNumFld1')[0]?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 1',
      },
      {
        key: 'extranumeric2',
        desc: this.settingsService.getSavedSettings('quoteConfNumFld2')[0]
          ? this.settingsService.getSavedSettings('quoteConfNumFld2')[0]?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 2',
      },
      {
        key: 'extranumeric3',
        desc: this.settingsService.getSavedSettings('quoteConfNumFld3')[0]
          ? this.settingsService.getSavedSettings('quoteConfNumFld3')[0]?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 3',
      },
      {
        key: 'extrastr1',
        desc: this.settingsService.getSavedSettings('quoteConfStrFld1')[0]
          ? this.settingsService.getSavedSettings('quoteConfStrFld1')[0]?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 4',
      },
      {
        key: 'extrastr2',
        desc: this.settingsService.getSavedSettings('quoteConfStrFld2')[0]
          ? this.settingsService.getSavedSettings('quoteConfStrFld2')[0]?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 5',
      },
      {
        key: 'extrastr3',
        desc: this.settingsService.getSavedSettings('quoteConfStrFld3')[0]
          ? this.settingsService.getSavedSettings('quoteConfStrFld3')[0]?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 6',
      },
      {
        key: 'extraDrpDown1',
        desc: this.settingsService.getSavedSettings('quoteConfDrpDownFld1')[0]
          ? this.settingsService.getSavedSettings('quoteConfDrpDownFld1')[0]
              ?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 7',
      },
      {
        key: 'extraDrpDown2',
        desc: this.settingsService.getSavedSettings('quoteConfDrpDownFld2')[0]
          ? this.settingsService.getSavedSettings('quoteConfDrpDownFld2')[0]
              ?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 8',
      },
      {
        key: 'extraDrpDown3',
        desc: this.settingsService.getSavedSettings('quoteConfDrpDownFld3')[0]
          ? this.settingsService.getSavedSettings('quoteConfDrpDownFld3')[0]
              ?.value
          : this.translateService.instant('ProdConfigurator.extrFld') + ' 9',
      },
      {
        key: 'qr-scanner-product',
        desc: this.translateService.instant(
          'QuoteConfigurator.qrScannerProduct'
        ),
      },
      {
        key: 'qr-scanner-warehouse',
        desc: this.translateService.instant(
          'QuoteConfigurator.qrScannerWarehouse'
        ),
      },
      {
        key: 'productBatch',
        desc: this.translateService.instant('QuoteConfigurator.productBatch'),
      },
      {
        key: 'del',
        desc: this.translateService.instant('QuoteConfigurator.del'),
      },
      {
        key: 'order',
        desc: this.translateService.instant('QuoteConfigurator.orderLine'),
      },
      {
        key: 'product-warehouse-stock',
        desc: this.translateService.instant(
          'QuoteConfigurator.productWarehouseStock'
        ),
      },
    ];
  }

  public getWarehouses() {
    return this.erpWarehouseService
      .getActiveWarehouses()
      .pipe(take(1))
      .subscribe((res) => {
        this.productListWarehouses.set(
          this.erpWarehouseService.filterWarehouses(res)
        );
        return true;
      });
  }

  checkForAddressField(allAttr: any[]) {
    const addressAttr = allAttr.find((x) => {
      return x.fld_code === 'singleEntity' && x.for_entity === 'address';
    });
    if (addressAttr && this.addressId !== addressAttr?.value) {
      this.addressId = addressAttr.value;
      if (this.addressId) {
        this.getAddressItem(this.addressId);
      } else {
        this.addressItem = null;
      }
    }
  }

  private getAddressItem(addressId: string) {
    this.entityService
      .geEntityItem('address', addressId)
      .pipe(take(1))
      .subscribe((resItem) => {
        this.addressItem = resItem;
      });
  }

  fillProdLine(item: any, line: ConfQuoteLine) {
    line.product = item;
    line.product_id = item.id;
    line.price = +(item?.product_price_per_piece ?? 0);

    let kathestosPerioxis = this.addressItem?.data?.find(
      (x) => x.fld_code_ent == 'address_fpa'
    )?.value;

    const vatLookupTable = {
      'Κανονικός 24% + Κανονικό': 24,
      'Κανονικός 24% + Μειωμένο (-30%)': 17,
      'Κανονικός 24% + Χωρίς ΦΠΑ': 0,
      'Μειωμένος 13% + Κανονικό': 13,
      'Μειωμένος 13% + Μειωμένο (-30%)': 9,
      'Μειωμένος 13% + Χωρίς ΦΠΑ': 0,
      'Υπερμειωμένος 6% + Κανονικό': 6,
      'Υπερμειωμένος 6% + Μειωμένο (-30%)': 4,
      'Υπερμειωμένος 6% + Χωρίς ΦΠΑ': 0,
    };

    const combinedKey = item.product_vat + ' + ' + kathestosPerioxis;
    line.vat = vatLookupTable[combinedKey] ?? 24;
    return line;
  }

  resetAddressData() {
    this.addressId = '';
    this.addressItem = null;
  }

  exportProdListToExcel(selectedColumns: string[], ITEMID: number, ITEM) {
    const gridColumns = selectedColumns.filter(
      (column) => column !== 'checkbox'
    );
    this.entityService
      .exportProdlistToExcel(ITEMID, ITEM.prodlines, gridColumns)
      .pipe(take(1))
      .subscribe((response: Blob | null) => {
        if (response !== null) {
          const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          this.fileUploadService.downloadToDevice(blob, 'prodlist-data.xlsx');
        }
      });
  }

  checkForContainerRounding(confQuote: ConfQuote) {
    confQuote.quotelines.forEach((line) => {
      // Calculate the container number. Must be an integer
      if (
        line.product !== null &&
        line.product.hasOwnProperty('product_alternate_mu') &&
        line.product.product_alternate_mu !== null &&
        line.product.hasOwnProperty('product_box_available') &&
        line.product.product_box_available == 1
      ) {
        line.containers = Math.ceil(
          parseFloat(
            (line.qty / Number(line.product.product_alternate_mu)).toFixed(2)
          )
        );
        setTimeout(() => {
          line.qty = parseFloat(
            (line.product.product_alternate_mu * line.containers).toFixed(2)
          );
        }, 5000);
      }
    });
  }

  getProductFromScanner(
    productCode: number,
    productFieldsForScanner: Field[]
  ): Promise<any | null> {
    const filter = new EntityItemFilter();
    const searchField = this.settingsService.getSavedSettings(
      'productFieldForQrCode'
    )[0];
    const productCodeField = productFieldsForScanner.filter(
      (field) => field.id === Number(searchField.value)
    )[0];
    productCodeField.search_from = String(productCode);
    filter.searchFields = [productCodeField];

    return firstValueFrom(
      this.entityService.getEntityDataList(filter, 'product')
    ).then((res) => {
      if (res.data.length > 0) {
        this.notificationService.showSnackbarMessage(
          'Messages.selectedProductForProdList'
        );
        this.entityService.serializeData(res);
        return res.data[0];
      } else {
        this.notificationService.showSnackbarMessage(
          'Messages.noProductForProdList'
        );
        return null;
      }
    });
  }

  validateProductWithScanner(productCode: any, line: ConfQuoteLine) {
    const searchFieldId = Number(
      this.settingsService.getSavedSettings('productFieldForQrCode')[0].value
    );
    this.quoteService
      .validateProduct(line.product_id, searchFieldId, productCode)
      .pipe(take(1))
      .subscribe((res) => {
        if (res.validated) {
          this.notificationService.showSnackbarMessage(
            'Messages.validatedProductSuccess'
          );
        } else {
          this.notificationService.showSnackbarMessage(
            'Messages.validatedProductFail'
          );
        }
      });
  }

  fetchSelectedColumns(
    localstorageKey: string,
    ITEM: any,
    displayedColumns: string[],
    productFields: Field[]
  ) {
    let savedColumnsJSON = localStorage.getItem(localstorageKey);
    if (savedColumnsJSON) {
      let savedColumns = JSON.parse(savedColumnsJSON).filter(
        (x) =>
          x != 'deactivateBulkAction' &&
          x != 'activateBulkAction' &&
          x != 'editBulkAction'
      );
      const cols = [];

      for (let savedColumn of savedColumns) {
        if (
          displayedColumns.find((x) => x === savedColumn) ||
          productFields.find((x) => x.fld_code === savedColumn)
        ) {
          cols.push(savedColumn);
        }
      }
      return cols;
    }
    if (
      ITEM?.fld_code_ent?.startsWith('pickingorder') ||
      ITEM?.fld_code_ent?.startsWith('receivinglist')
    ) {
      return ProductListHelper.fetchPORLDisplayColumns();
    }
    if (ITEM?.fld_code_ent?.startsWith('inventory')) {
      return ProductListHelper.fetchInventoryDisplayColumns();
    }
    return ProductListHelper.fetchDefaultDisplayColumns();
  }

  createNewLine(item: any, confQuote: ConfQuote, selectedWarehouseId: number) {
    const newLine: ConfQuoteLine = new ConfQuoteLine();
    newLine.product = item;
    newLine.availability = 1;
    newLine.qty = 1;
    newLine.numFld1 = 0;
    newLine.discount = confQuote.totalDiscount;
    newLine.discount2 = 0;
    newLine.product_id = item?.id ?? null;
    newLine.unit = MeasurementUnit.Piece;
    newLine.aa = confQuote.quotelines.length + 1;
    newLine.price = +(item?.product_price_per_piece ?? 0);
    newLine.vat = 24;
    newLine.warehouse_id = selectedWarehouseId ?? null;
    return newLine;
  }

  changeUnit(line: ConfQuoteLine) {
    if (+line.unit == MeasurementUnit.Piece) {
      line.price = +line.product.product_price_per_piece;
    } else if (+line.unit == MeasurementUnit.SmallBox) {
      line.price = +line.product.product_price_per_small_box;
    } else if (+line.unit == MeasurementUnit.Box) {
      line.price = +line.product.product_price_per_box;
    } else if (+line.unit == MeasurementUnit.Palet) {
      line.price = +line.product.product_price_per_palet;
    }
  }

  calculateTotalPrice(confQuote: ConfQuote) {
    confQuote.totalAmount = 0;
    for (let line of confQuote.quotelines) {
      if (!line.product) {
        continue;
      }
      confQuote.totalAmount +=
        this.productListLineTotalPricePipe.transform(line);
    }
    confQuote.totalAmount = parseFloat(confQuote.totalAmount.toFixed(2));
  }

  filterProductFields(fields: Field[]) {
    return fields.filter(
      (x) =>
        x.isGridColumn == 1 &&
        x.fld_code != 'product_name' &&
        x.fld_code != 'piecePrice' &&
        x.fld_code != 'smallBoxPrice' &&
        x.fld_code != 'boxPrice' &&
        x.fld_code != 'paletPrice' &&
        x.fld_code != 'nettoPrice' &&
        x.fld_code != 'stock'
    );
  }

  updateSelectedColumns(selectedColumns: string[], $event: string[]) {
    const difference = selectedColumns
      .filter((item) => !$event.includes(item))
      .concat($event.filter((item) => !selectedColumns.includes(item)))[0];
    if (selectedColumns.includes(difference)) {
      return selectedColumns.filter((item) => item !== difference);
    } else {
      return [...selectedColumns, difference];
    }
  }

  updateTotalValueEntityField(allAttr: any[], totalAmount: number) {
    const totalValueField = allAttr.find((x) => {
      return ['quote_total_value', 'order_total_value'].includes(
        x.fld_code_ent
      );
    });
    if (totalValueField) {
      totalValueField.value = totalAmount;
    }
  }

  storeSelectedColumnsToLocalstorage(
    selectedColumns: string[],
    localstorageKey: string
  ) {
    const delIndex = selectedColumns.findIndex((x) => x === 'del');
    if (delIndex > -1) {
      selectedColumns.splice(delIndex, 1);
      selectedColumns.push('del');
    }
    localStorage.setItem(localstorageKey, JSON.stringify(selectedColumns));
  }

  replaceLineOrAddNew(
    confQuote: ConfQuote,
    linesToAdd: ConfQuoteLine[],
    line: ConfQuoteLine
  ) {
    const index = confQuote.quotelines.findIndex((l) => l === line);
    if (index !== -1) {
      confQuote.quotelines.splice(index, 1, ...linesToAdd);
    } else {
      confQuote.quotelines.push(...linesToAdd);
    }
  }

  sortProductLinesBasedOnWarehouses(res: ConfQuoteLine[]) {
    const warehousePriorityMap = new Map<number, number | null>();

    // Build a map: warehouse_id -> priority
    this.productListWarehouses().forEach((w) => {
      warehousePriorityMap.set(w.id, w.priority);
    });

    return res.slice().sort((a, b) => {
      const priorityA = warehousePriorityMap.get(a.warehouse_id ?? -1);
      const priorityB = warehousePriorityMap.get(b.warehouse_id ?? -1);

      // Handle missing warehouse_id (undefined) or missing priority (null)
      const hasPriorityA = priorityA !== null && priorityA !== undefined;
      const hasPriorityB = priorityB !== null && priorityB !== undefined;

      if (hasPriorityA && hasPriorityB) {
        return priorityA! - priorityB!; // Compare priorities normally
      } else if (hasPriorityA) {
        return -1; // a has priority, b doesn't -> a comes first
      } else if (hasPriorityB) {
        return 1; // b has priority, a doesn't -> b comes first
      } else {
        return 0; // neither has priority -> keep their order
      }
    });
  }

  setWarehouseForLine(
    line: ConfQuoteLine,
    code: any,
    confQuoteLines: ConfQuoteLine[]
  ) {
    const warehouseFromScanner = this.productListWarehouses().find(
      (w) => w.code === code
    );
    if (warehouseFromScanner) {
      this.notificationService.showSnackbarMessage('Messages.warehouseFound');
      const updatedLine = { ...line };
      updatedLine.warehouse_id = warehouseFromScanner?.id;
      const updatedConfQuoteLines = [...confQuoteLines];
      const index = confQuoteLines.findIndex((q) => q.aa === updatedLine.aa);
      if (index !== -1) {
        updatedConfQuoteLines[index] = updatedLine;
      }
      return updatedConfQuoteLines;
    } else {
      this.notificationService.showSnackbarMessage(
        'Messages.warehouseNotFound'
      );
      return confQuoteLines;
    }
  }

  getProductFields() {
    this.entityService
      .getFldsEntityByName('product')
      .pipe(take(1))
      .subscribe((res) => {
        this.productFields.set(res.fields);
      });
  }

  openProductWarehouseStockPopUp(line: ConfQuoteLine) {
    this.dialog.open(ProductWarehouseStockInfoComponent, {
      data: line,
      height: '80%',
      width: '80%',
    });
  }

  fetchProductStockInfo(productId: number) {
    this.warehouseService
      .getProductWhStocks(productId)
      .pipe(take(1))
      .subscribe((res) => {
        this.productStocksPerWarehouse.set(res);
      });
  }

  fetchWarehouseName(warehouse_id: number) {
    return (
      this.productListWarehouses().find((w) => w.id === warehouse_id).title ??
      ''
    );
  }

  getWarehouseProductStocks(warehouseId: number) {
    this.warehouseService
      .getWarehouseProductStocks(warehouseId)
      .pipe(take(1))
      .subscribe((res) => {
        console.log(res);
        this.warehouseProductStocks.set(res);
      });
  }
}
